//
// Standalone side-menu "Help" entry (not a navigable page). Clicking it unfolds
// three sub-entries styled like a real page submenu, in increasing scope:
// Explain (toggles explain mode), Tutorials (opens the tutorials popup) and
// Guide (navigates to the guide page). id #help is the welcome's anchor.
//
// While explain mode is on, the Explain entry grows the mode's own controls
// underneath it. They live here rather than in an overlay on the page: a floating
// bar sat on top of whatever the page puts at its bottom edge, and the buttons it
// covered were exactly the ones the markers were describing.
//

import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useEffect,
  useState,
} from 'react';
import {
  Link,
  useLocation,
} from 'react-router-dom';

import {
  useTownAssets,
  useTutorial,
} from '../stores';

import SvgColorImg from './SvgColorImg';
import TutorialsFlyout from './TutorialsFlyout';

const subItemClass = 'w-full flex flex-row gap-4 items-center justify-start relative py-2 no-underline text-inherit';
const explainLineClass = 'w-full flex flex-row gap-2 items-center relative z-10 pl-20 pr-3 py-1 '
  + 'text-sm text-left text-pxTxt whitespace-nowrap bg-transparent border-0 cursor-pointer';

// Mirrors the markup of the page submenus rendered in SideMenu (filler icon at
// ml-10, selected outline, recess background) so Help's sub-entries look native.
// `active` is the on state of a mode: the text goes dark, and the caller paints the
// teal block behind the entry and the mode's own lines.
function SubItem({
  label, selected, onClick, to, id, active, className: extraClassName,
}) {
  const className = classNames(
    subItemClass,
    {
      'fill-pxMainMenuItem': !(selected || active),
      'fill-pxTxt': selected || active,
    },
    extraClassName,
  );

  const inner = (
    <>
      {selected
        ? (
            <div className="absolute t-0 l-0 ml-16 h-full w-full py-0.5 pointer-events-none">
              <div className="bg-pxMainBgnd rounded-l-md h-full w-full" />
            </div>
          )
        : null}

      <div className="ml-10 w-5 h-5 z-10 relative shrink-0" />

      <span className="z-10 relative flex items-center gap-1.5 min-w-0 flex-1 pr-3">
        <p
          className={classNames(
            'text-base truncate',
            { 'font-semibold text-pxTxt': selected || active },
          )}
        >
          {label}
        </p>

      </span>
    </>
  );

  if (to) {
    return (
      <Link to={to} id={id} className={className} data-help-anchor>
        {inner}
      </Link>
    );
  }

  return (
    <button
      type="button"
      id={id}
      onClick={onClick}
      aria-pressed={active}
      data-help-anchor
      className={classNames(className, 'bg-transparent border-0 cursor-pointer text-left')}
    >
      {inner}
    </button>
  );
}

SubItem.propTypes = {
  label: PropTypes.string,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  to: PropTypes.string,
  id: PropTypes.string,
  active: PropTypes.bool,
  className: PropTypes.string,
};

function HelpMenu() {
  const { assets } = useTownAssets();
  const { translate } = assets;
  const { tutorial } = useTutorial();
  const {
    explainOn, setExplainMode, skip, setSkip,
  } = tutorial;
  const location = useLocation();
  const currentPage = location.pathname.split('/')[1] || 'home';
  const onGuide = (currentPage === 'guide');
  const [toggled, setToggled] = useState(false);
  const [tutorialsAnchor, setTutorialsAnchor] = useState(null);

  // Navigating closes the tutorials popup and folds the menu; the guide page
  // keeps the menu open via onGuide below.
  useEffect(
    () => {
      setToggled(false);
      setTutorialsAnchor(null);
    },
    [location.pathname],
  );

  // Leaving explain mode folds the menu, whichever way the mode was left: the entry
  // was often unfolded by hand to reach Expliquer, and would otherwise stay open.
  useEffect(
    () => {
      if (!explainOn) setToggled(false);
    },
    [explainOn],
  );

  // Pinned (un-foldable) on the guide page and while explain mode is on.
  const pinned = onGuide || explainOn;
  const expanded = toggled || pinned;

  const openTutorials = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTutorialsAnchor((cur) => (cur ? null : rect));
  };

  return (
    <>
      <div id="helpSection" className="w-full">
        <button
          type="button"
          id="help"
          data-help-anchor
          onClick={() => setToggled((cur) => {
            if (cur) setTutorialsAnchor(null);
            return !cur;
          })}
          className="w-full flex flex-row gap-4 items-center justify-start relative py-2 text-left text-inherit bg-transparent border-0 cursor-pointer fill-pxMainMenuItem"
        >
          <span className="relative ml-7 shrink-0 z-10">
            <SvgColorImg
              className="object-contain w-5 h-5 fill-pxMainMenuItem"
              src="/images/svg/gui/question.svg"
              description={translate('helpLabel', 'tutorial')}
              imgColorAsCssFill
            />
          </span>

          <p className="truncate z-10 flex-1">{translate('helpLabel', 'tutorial')}</p>

          <SvgColorImg
            src="/images/svg/gui/arrowDown.svg"
            description="."
            imgColorAsCssFill
            className={classNames(
              'w-4 h-4 mr-3 shrink-0 fill-pxMainMenuItem transition-transform',
              expanded ? 'rotate-none' : '-rotate-90',
              { 'opacity-40': pinned },
            )}
          />
        </button>

        {expanded
          ? (
              <div className="relative flex flex-col w-full">
                <div className="absolute top-0 left-0 ml-3 h-full w-full pointer-events-none bg-menuRecess" />

                {/* The Explain entry and, while the mode runs, its own controls: one
                    block, which is what the welcome points at when it explains the mode. */}
                <div id="explainSection" className="relative">
                  {explainOn
                    ? (
                        <div className="absolute inset-y-0 left-0 ml-16 w-full py-0.5 pointer-events-none">
                          <div className="bg-pxHelpLight rounded-l-md h-full w-full" />
                        </div>
                      )
                    : null}

                  {/* The way out is a sibling of the entry, not nested in it, and both */}
                  {/* sit in one row: on a narrow menu the entry name gives way first. */}
                  <div className="flex flex-row items-start">
                    <SubItem
                      id="explainEntry"
                      className="flex-1 min-w-0"
                      label={translate('explainLabel', 'tutorial')}
                      active={explainOn}
                      onClick={() => {
                        setTutorialsAnchor(null);
                        setExplainMode(!explainOn);
                      }}
                    />

                    {explainOn
                      ? (
                          <button
                            type="button"
                            onClick={() => setExplainMode(false)}
                            data-help-anchor
                            className="relative z-10 shrink-0 py-2 pr-3 text-base text-pxTxt underline whitespace-nowrap bg-transparent border-0 cursor-pointer"
                          >
                            {translate('done')}
                          </button>
                        )
                      : null}
                  </div>

                  {explainOn
                    ? (
                        <>
                          <label className={`${explainLineClass} pb-2`} data-help-anchor>
                            <input
                              type="checkbox"
                              checked={!skip}
                              onChange={(event) => setSkip(!event.target.checked)}
                            />
                            <span>{translate('automatic')}</span>
                          </label>
                        </>
                      )
                    : null}
                </div>

                <SubItem
                  id="tutorialsEntry"
                  label={translate('tutorials')}
                  selected={Boolean(tutorialsAnchor)}
                  onClick={openTutorials}
                />

                <SubItem
                  label={translate('guideLabel', 'tutorial')}
                  selected={onGuide}
                  to="/guide"
                />
              </div>
            )
          : null}
      </div>

      {tutorialsAnchor
        ? <TutorialsFlyout anchorRect={tutorialsAnchor} onClose={() => setTutorialsAnchor(null)} />
        : null}
    </>
  );
}

export default HelpMenu;
